import { TextInput, WithErrorForm } from "components/common";
import { Modal, ModalProps } from "components/common/Modal";
import { StyledButtonArea } from "components/groupManagement/GroupList/styled";
import { ButtonPresenter } from "components/parts/Button/Button/Button";
import { Typography } from "components/parts/Typography/Typography";
import { useFormik } from "formik";
import { useLoadingState } from "providers/LoadingProvider";
import React from "react";
import { useHistory } from "react-router";
import { updatePassword } from "utils/auth";
import { validationSchemaPasswordChange } from "utils/validation";

import {
  StyledForm,
  StyledFormRow,
  StyledModalContent,
  StyledModalFooter,
  StyledModalHeader,
} from "./styled";

type PasswordChangeModalProps = {
  closePasswordChangeModal: () => void;
} & ModalProps;

type PasswordChangeFormValueType = {
  oldpassword: string;
  password1: string;
  password2: string;
};

export const PasswordChangeModal: React.FC<PasswordChangeModalProps> = ({
  closePasswordChangeModal,
  closeHandler,
}) => {
  const { setLoading } = useLoadingState();
  const history = useHistory();

  const changePassword = async (
    values: PasswordChangeFormValueType
  ): Promise<boolean> => {
    let ret = true;
    await updatePassword(values.oldpassword, values.password1)
      .then(() => {
        alert("パスワードを変更しました。");
        setLoading(false);
      })
      .catch((e) => {
        switch (e.code) {
          case "NotAuthorizedException":
            // oldPassword が誤っている場合に起こる。
            alert("旧パスワードが間違っています。");
            break;
          case "LimitExceededException":
            // oldPassword を間違え続けた場合に起こる。
            alert("旧パスワードを規定回数以上間違えました。");
            break;
          case "InvalidPasswordException":
            // ユーザープールのポリシーで設定したパスワードの強度を満たさない場合に起こる。
            alert("パスワードの要件を満たしていません。");
            break;
          case "InvalidParameterException":
            // password が6文字未満など Cognito 側で正しくパースできない場合（バリデーションエラー）に起こる。
            alert("パスワードの要件を満たしていません。");
            break;
          default:
            // その他のエラー
            console.error(e);
            alert("エラーが発生しました。");
            history.replace("/auth/login");
            break;
        }
        setLoading(false);
        ret = false;
      });

    return ret;
  };

  const onSubmit = async (values: PasswordChangeFormValueType) => {
    setLoading(true);
    const ret = await changePassword(values);
    if (ret) closePasswordChangeModal();
  };

  const formik = useFormik<PasswordChangeFormValueType>({
    initialValues: {
      oldpassword: "",
      password1: "",
      password2: "",
    },
    validationSchema: validationSchemaPasswordChange,
    onSubmit: onSubmit,
  });

  return (
    <Modal closeHandler={closeHandler}>
      <StyledForm onSubmit={formik.handleSubmit}>
        <StyledModalHeader>
          <Typography text="パスワード変更" size="large" />
        </StyledModalHeader>
        <StyledModalContent>
          <StyledFormRow>
            <Typography text="旧パスワード" />
            <WithErrorForm
              touched={formik.touched.oldpassword}
              errorMessage={formik.errors.oldpassword}
            >
              <TextInput
                autoFocus
                {...formik.getFieldProps("oldpassword")}
                width="15rem"
                type="password"
                maxlength="15"
              />
            </WithErrorForm>
          </StyledFormRow>
          <StyledFormRow>
            <Typography text="新パスワード" />
            <WithErrorForm
              touched={formik.touched.password1}
              errorMessage={formik.errors.password1}
            >
              <TextInput
                {...formik.getFieldProps("password1")}
                type="password"
                width="15rem"
                maxlength="15"
              />
            </WithErrorForm>
          </StyledFormRow>
          <StyledFormRow>
            <Typography text="新パスワード（確認）" />
            <WithErrorForm
              touched={formik.touched.password2}
              errorMessage={formik.errors.password2}
            >
              <TextInput
                {...formik.getFieldProps("password2")}
                type="password"
                width="15rem"
                maxlength="15"
              />
            </WithErrorForm>
          </StyledFormRow>
        </StyledModalContent>
        <StyledModalFooter>
          <StyledButtonArea>
            <ButtonPresenter text={"変更"} type="submit" />
          </StyledButtonArea>
        </StyledModalFooter>
      </StyledForm>
    </Modal>
  );
};
